// Single entry — Vite extracts the SCSS into a <link> in <head>.
import '../scss/master.scss';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const supportsHover = window.matchMedia('(hover: hover)').matches;

// Lenis (Phase 2B) was removed in Phase 2E as a diagnostic — Phase 1.2
// pin behaviour worked correctly with native scroll, and the post-2B
// reports of services/.history overlap correlated with Lenis being
// in the chain. If/when smooth scroll is wanted again, re-add via
// ScrollTrigger.scrollerProxy() for proper integration.

// ---------- Animated noise overlay -----------------------------------------
// Translucent grain layer fixed to the viewport. Renders at HALF the
// device resolution (CSS-scaled back up) so each frame paints 4× fewer
// pixels — visually identical because noise is, well, random — while
// dropping the CPU cost from "audible fan spin on a MacBook Air" to
// "free." Reduced-motion users get a single static frame.
function setupNoise() {
  const canvas = document.getElementById('noiseCanvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  // Downsample factor — 2 = quarter the pixels per frame, identical look
  const SCALE = 2;

  const resize = () => {
    canvas.width  = Math.ceil(window.innerWidth  / SCALE);
    canvas.height = Math.ceil(window.innerHeight / SCALE);
    // CSS keeps the canvas filling the viewport; the browser nearest-
    // neighbour-scales the smaller bitmap up, which on grain reads
    // exactly the same as native resolution.
    canvas.style.width  = '100%';
    canvas.style.height = '100%';
  };
  resize();
  window.addEventListener('resize', resize, { passive: true });

  const generate = () => {
    const imageData = ctx.createImageData(canvas.width, canvas.height);
    const buffer = new Uint32Array(imageData.data.buffer);
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const baseColor = isDark ? 0 : 255;

    for (let i = 0; i < buffer.length; i++) {
      const grayscale = Math.random() * 255;
      const alpha = Math.random() * 128 + 128;
      const channel = ((baseColor + grayscale) / 3) | 0;
      buffer[i] = (alpha << 24) | (channel << 16) | (channel << 8) | channel;
    }

    ctx.putImageData(imageData, 0, 0);
  };

  generate();
  if (!reducedMotion) {
    let rafId;
    const loop = () => {
      generate();
      rafId = requestAnimationFrame(loop);
    };
    loop();
    // Vite HMR: cancel the rAF loop on hot-replace so we don't accumulate
    // multiple noise loops in dev (they'd compete for the canvas).
    if (import.meta.hot) {
      import.meta.hot.dispose(() => cancelAnimationFrame(rafId));
    }
  }

  // Keep grain colour matched to current theme.
  document.addEventListener('themechange', generate);
}

// ---------- Theme toggle (light / dark) ------------------------------------
function setupThemeToggle() {
  const toggle = document.querySelector('.dark-mode-toggle');
  if (!toggle) return;

  const stored = localStorage.getItem('bsTheme') || 'dark';
  document.documentElement.setAttribute('data-theme', stored);
  toggle.classList.toggle('dark-mode', stored === 'dark');
  toggle.setAttribute('role', 'switch');
  toggle.setAttribute('aria-checked', String(stored === 'dark'));
  toggle.setAttribute('aria-label', 'Toggle dark mode');

  toggle.addEventListener('click', () => {
    const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    toggle.classList.toggle('dark-mode', next === 'dark');
    toggle.setAttribute('aria-checked', String(next === 'dark'));
    localStorage.setItem('bsTheme', next);
    document.dispatchEvent(new CustomEvent('themechange', { detail: { theme: next } }));
  });
}

// ---------- Show .title in nav once user scrolls past the hero -------------
function setupTitleScroll() {
  if (window.innerWidth < 768) return;
  const titles = document.querySelectorAll('.title');
  if (!titles.length) return;
  const onScroll = () => {
    const show = window.scrollY > 767;
    titles.forEach((el) => el.classList.toggle('show-title', show));
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ---------- Vanilla navbar collapse (replaces bootstrap.bundle.min.js) -----
function setupNavToggle() {
  const toggler = document.querySelector('.nav-toggle');
  const target = document.getElementById('navbarNav');
  if (!toggler || !target) return;

  const setOpen = (open) => {
    target.classList.toggle('show', open);
    toggler.setAttribute('aria-expanded', String(open));
  };

  toggler.addEventListener('click', () => setOpen(!target.classList.contains('show')));

  // Close when a nav link is followed (mobile UX)
  target.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => setOpen(false));
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && target.classList.contains('show')) setOpen(false);
  });
}

// ---------- GSAP scroll-driven animations ----------------------------------
// All scroll-driven motion lives behind gsap.matchMedia(). When a query stops
// matching (e.g. user resizes 1440 → 768), GSAP runs the scoped cleanup,
// kills the ScrollTriggers, and clears any inline transforms. This is the
// canonical fix for the "horizontal timeline stuck at negative X on mobile"
// bug — the previous code re-killed triggers on resize but never reset the
// inline `transform` left behind by `gsap.to(el, { x: ... })`.
function setupAnimations() {
  // Defensive: clear any ScrollTriggers + pin-spacers left over from a
  // previous run (e.g. dev-server HMR re-running this module). Without
  // this, multiple HMR cycles can accumulate stacked pin-spacers around
  // .history, which renders as a visible "duplicate" of the section.
  ScrollTrigger.getAll().forEach((t) => t.kill(true));

  // Reduced-motion users get a single fade-up for the bio words; no scrub,
  // no parallax, no horizontal pin. Everything else is static.
  if (reducedMotion) {
    const el = document.querySelector('.split-word');
    if (el) {
      const split = new SplitText(el, { type: 'words' });
      gsap.set(split.words, { opacity: 1 });
    }
    return;
  }

  // SplitText reveal (all viewports). Word-by-word fade from 0.5 → 1
  // opacity with a color tween, scrubbed to scroll. Re-runs on theme
  // change so the words land at the correct end-color.
  let split;
  let splitTl;

  const buildSplit = () => {
    split && split.revert();
    splitTl && splitTl.kill();

    const target = document.querySelector('.split-word');
    if (!target) return;

    split = new SplitText(target, { type: 'words' });
    const colour = document.documentElement.getAttribute('data-theme') === 'dark'
      ? '#fff'
      : '#000';

    splitTl = gsap.timeline({
      scrollTrigger: {
        trigger: '.split-word',
        start: 'top 70%',
        end: 'bottom 80%',
        scrub: 1,
      },
    });

    splitTl.fromTo(
      split.words,
      { opacity: 0.5 },
      { opacity: 1, color: colour, stagger: 0.1, duration: 1, ease: 'power2.out' }
    );
  };

  buildSplit();
  document.addEventListener('themechange', buildSplit);

  const mm = gsap.matchMedia();

  // Hero parallax + blur — every viewport.
  mm.add('(min-width: 1px)', () => {
    gsap.to('.hero-img img', {
      y: '-300px',
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero-text',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });

    gsap.fromTo(
      '.hero-img img',
      { filter: 'blur(0px)' },
      {
        filter: 'blur(30px)',
        opacity: 0.5,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.hero-text',
          start: '10% top',
          end: 'bottom+=1 top',
          scrub: true,
        },
      }
    );
  });

  // Hero aside pin — desktop only.
  mm.add('(min-width: 1024px)', () => {
    ScrollTrigger.create({
      trigger: '.hero-text',
      start: 'top top',
      end: 'bottom bottom',
      pin: '.hero-aside',
      pinSpacing: false,
    });
  });

  // Horizontal timeline — desktop only.
  // Below 1024px the SCSS handles the layout: tablet (641–1023) gets a
  // native touch-swipe with scroll-snap; phone (≤640) stacks vertically.
  // Phones never enter this branch, so there's no GSAP transform to leak.
  mm.add('(min-width: 1024px)', () => {
    const horizontalTimeline = document.querySelector('.horizontal-timeline');
    if (!horizontalTimeline) return;

    // ONE ScrollTrigger that both pins .history AND drives the timeline's
    // X transform. Two separate triggers fighting for the same pin position
    // caused GSAP to misfire on engagement (the Phase 1.0 / 1.1 "duplicate
    // history" bug — .services items 4–5 visibly overlapped the pinned
    // .history because the pin engaged before .services had fully scrolled
    // out of view).
    //
    // Pin distance = (scrollWidth - innerWidth) — the actual scroll needed
    // to reveal the off-screen tail of the timeline. The previous code used
    // `+= scrollWidth` which left an extra `innerWidth` of dead-zone scroll
    // where the pinned section sat static (the user's "massive space").
    gsap.to(horizontalTimeline, {
      x: () => -(horizontalTimeline.scrollWidth - window.innerWidth),
      ease: 'none',
      scrollTrigger: {
        trigger: '.history',
        start: 'top top',
        end: () => '+=' + (horizontalTimeline.scrollWidth - window.innerWidth),
        pin: true,
        scrub: 1,
        // Recalculate on viewport changes so layout shifts (font load, etc.)
        // don't leave the pin captured at a stale scroll position.
        invalidateOnRefresh: true,
      },
    });

    // Cleanup: when this query no longer matches (resize down to tablet/phone)
    // GSAP auto-kills the ScrollTrigger above and runs this fn — clear the
    // inline transform so the native scroll layout doesn't start at -1500px.
    return () => {
      gsap.set(horizontalTimeline, { clearProps: 'transform,x' });
    };
  });

  // Refresh ScrollTrigger after every milestone where layout could shift.
  // Without these, ScrollTrigger caches element positions BEFORE fonts /
  // images / videos have decoded, then pin triggers fire at the wrong
  // scrollY — symptom: pinned section overlaps the section above it
  // because the pin engaged 100–200 px too early.
  //
  //   1. requestAnimationFrame: immediately after init, in case layout
  //      already settled (cached fonts, instant CSS load)
  //   2. document.fonts.ready: after Manrope finishes loading and the
  //      browser reflows the headings to the final glyph metrics
  //   3. window 'load': fires after every sub-resource (images, videos,
  //      iframes) has resolved
  //   4. ResizeObserver on <body>: catches every other layout shift —
  //      Vite's CSS hot-injection in dev (CSS arrives after JS, layout
  //      jumps), late image decode, dynamic content. THIS is the one
  //      that fixes the dev-only services/history overlap: in
  //      production CSS is in <head> as blocking <link>, so layout is
  //      stable before JS runs — but Vite's dev server injects CSS via
  //      JS modules *after* setupAnimations() has already captured pin
  //      positions against the un-styled layout.
  requestAnimationFrame(() => ScrollTrigger.refresh());
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }
  window.addEventListener('load', () => ScrollTrigger.refresh());

  if (typeof ResizeObserver !== 'undefined') {
    // ScrollTrigger.refresh() recalculates pin-spacer dimensions, which
    // can change body.height — re-triggering ResizeObserver and looping
    // forever. Use lastHeight tracking + a refreshing flag to break the
    // loop, and only react to *real* layout changes (not the side-effect
    // of refresh() itself).
    let throttle;
    let lastHeight = document.body.offsetHeight;
    let refreshing = false;
    const ro = new ResizeObserver(() => {
      if (refreshing) return;
      const h = document.body.offsetHeight;
      if (h === lastHeight) return;
      lastHeight = h;
      clearTimeout(throttle);
      throttle = setTimeout(() => {
        refreshing = true;
        ScrollTrigger.refresh();
        // Update lastHeight to whatever refresh produced, so the next
        // ResizeObserver tick (caused by refresh itself) is a no-op.
        lastHeight = document.body.offsetHeight;
        refreshing = false;
      }, 150);
    });
    ro.observe(document.body);
  }
}

// ---------- Magnetic cursor on CTAs ---------------------------------------
// Subtle pull on key links / buttons — they drift toward the cursor when
// it's near, snap back on leave. Hover-capable pointers only (touch
// devices skip this entirely; it'd never trigger anyway).
//
// Selector list is intentionally narrow: only "primary" CTAs that benefit
// from the affordance. Don't sprinkle this on every link or it stops
// reading as intentional.
function setupMagneticCursors() {
  if (!supportsHover || reducedMotion) return;

  const targets = document.querySelectorAll(
    '.cta-link, .play-btn, .footer-cta, .contact-row, .resume-link'
  );

  targets.forEach((el) => {
    let raf = 0;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        gsap.to(el, {
          x: x * 0.18,
          y: y * 0.18,
          duration: 0.45,
          ease: 'power3.out',
          overwrite: 'auto',
        });
      });
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      gsap.to(el, {
        x: 0,
        y: 0,
        duration: 0.6,
        ease: 'elastic.out(1, 0.55)',
        overwrite: 'auto',
      });
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
  });
}

// ---------- Footer year + viewer timezone ----------------------------------
function setupFooterMeta() {
  // Always-correct copyright year — no need to remember to bump the markup.
  const yearEl = document.querySelector('[data-year]');
  if (yearEl) yearEl.textContent = String(new Date().getFullYear());

  // Show the visitor's local timezone abbreviation next to the locale string,
  // so a UK-based viewer sees "GMT" / "BST" and an LA viewer sees "PST" etc.
  const tzEl = document.querySelector('[data-tz]');
  if (tzEl) {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' })
        .formatToParts(new Date());
      const tz = parts.find((p) => p.type === 'timeZoneName')?.value;
      if (tz) tzEl.textContent = tz;
    } catch {
      // Older browser — keep the markup default
    }
  }
}

// ---------- Init -----------------------------------------------------------
function init() {
  setupNoise();
  setupThemeToggle();
  setupTitleScroll();
  setupNavToggle();
  setupFooterMeta();
  setupMagneticCursors();
  setupAnimations();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Vite HMR cleanup — without this, every hot-replace would stack a fresh
// set of ScrollTriggers + pin-spacers on top of the existing ones, which
// renders as a visible "duplicate" of any pinned section (e.g. .history).
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    ScrollTrigger.getAll().forEach((t) => t.kill(true));
    // Remove any lingering pin-spacers (kill(true) usually does this but
    // guards against edge cases where the parent has been re-mounted).
    document.querySelectorAll('.pin-spacer').forEach((el) => {
      const child = el.firstElementChild;
      if (child) el.replaceWith(child);
    });
  });
}
